import { Label } from "../ui/label";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Loader2, Mail, Phone, User, UserIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { userApi } from "~/lib/api/user";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { Form, FormField, FormItem, FormMessage } from "../ui/form";
import { Input } from "../ui/input";

const personalInfoSchema = z.object({
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	email: z.string().email("Invalid email").optional(),
	phoneNumber: z.string().refine((val) => {
		const _val = Number(val);
		return !Number.isNaN(_val) && _val.toString().length === 10;
	}, "Invalid phone number"),
	username: z
		.string()
		.max(20, "Username must be less than 20 characters")
		.optional(),
});

type PersonalInfoForm = z.infer<typeof personalInfoSchema>;

export function PersonalInfo({
	firstName,
	lastName,
	email,
	phoneNumber,
	username,
}: PersonalInfoForm) {
	const router = useRouter();
	const [isEditing, setIsEditing] = useState(false);
	const { mutateAsync: updateProfile, isPending } = useMutation({
		mutationFn: (data: PersonalInfoForm) => {
			return userApi.updateProfile(data);
		},
		onSuccess: async ({ data }) => {
			if (data.username !== username) {
				await router.navigate({
					to: "/u/$profile",
					params: { profile: data.username },
					replace: true,
				});
			}
			await router.invalidate();
		},
	});
	const _phoneNumber = Number(phoneNumber);
	const form = useForm<PersonalInfoForm>({
		resolver: zodResolver(personalInfoSchema),
		defaultValues: {
			firstName,
			lastName,
			email,
			phoneNumber: Number.isNaN(_phoneNumber) ? "" : _phoneNumber.toString(),
			username,
		},
		disabled: !isEditing || isPending,
	});

	async function onSubmit(data: PersonalInfoForm) {
		await updateProfile(data);
		setIsEditing(false);
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<Card className="bg-white/5 border-white/10 overflow-hidden rounded-xl">
					<CardHeader className="p-5 border-b border-white/10 bg-white/5 flex items-center flex-row">
						<h3 className="text-lg font-medium flex items-center gap-2 text-white">
							<UserIcon className="w-4 h-4 text-accent" />
							Personal Information
						</h3>
						<Button
							variant="secondary"
							size="sm"
							className="ml-auto"
							onClick={() => {
								if (isEditing) {
									form.handleSubmit(onSubmit)();
								} else {
									setIsEditing(true);
								}
							}}
							type="button"
							disabled={isPending}
						>
							{isEditing ? "Save" : "Edit"}
							{isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
						</Button>
						{isEditing ? (
							<Button
								variant="secondary"
								size="sm"
								className="ml-2"
								onClick={() => {
									setIsEditing(false);
								}}
								type="button"
								disabled={isPending}
							>
								Cancel
							</Button>
						) : null}
					</CardHeader>
					<CardContent className="flex flex-col space-y-4 p-5">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<Label
											htmlFor="firstName"
											className="text-white flex items-center gap-2"
										>
											First Name
										</Label>
										<Input
											id="firstName"
											placeholder="Vivek"
											className={cn(
												"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
											)}
											disabled={field.disabled}
											{...field}
										/>
										<FormMessage className="text-red-500 text-sm mt-1" />
									</FormItem>
								)}
							/>
							<div className="space-y-2">
								<FormField
									control={form.control}
									name="lastName"
									render={({ field }) => (
										<FormItem>
											<Label
												htmlFor="lastName"
												className="text-white flex items-center gap-2"
											>
												Last Name
											</Label>
											<Input
												id="lastName"
												placeholder="Kumar"
												className={cn(
													"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
												)}
												disabled={field.disabled}
												{...field}
											/>
											<FormMessage className="text-red-500 text-sm mt-1" />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div className="space-y-2">
							<FormField
								control={form.control}
								name="phoneNumber"
								render={({ field }) => (
									<FormItem>
										<Label
											htmlFor="phoneNumber"
											className="text-white/70 text-sm flex items-center gap-1.5"
										>
											<Phone className="w-3.5 h-3.5 text-accent/70" />
											Phone Number
										</Label>
										<Input
											id="phoneNumber"
											placeholder="+91 9876543210"
											className={cn(
												"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
											)}
											disabled={field.disabled}
											{...field}
										/>
										<FormMessage className="text-red-500 text-sm mt-1" />
									</FormItem>
								)}
							/>
						</div>
						<div className="space-y-2">
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<Label
											htmlFor="username"
											className="text-white/70 text-sm flex items-center gap-1.5"
										>
											<User className="w-3.5 h-3.5 text-accent/70" />
											Username
										</Label>
										<Input
											id="username"
											placeholder="vivek.kumar"
											className={cn(
												"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
												field.disabled && "opacity-50",
											)}
											disabled={field.disabled}
											{...field}
										/>
										<FormMessage className="text-red-500 text-sm mt-1" />
									</FormItem>
								)}
							/>
						</div>
						<div className="space-y-2">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<Label
											htmlFor="email"
											className="text-white/70 text-sm flex items-center gap-1.5"
										>
											<Mail className="w-3.5 h-3.5 text-accent/70" />
											Email
											<div className="ml-1.5 text-xs px-1.5 py-0.5 bg-black/20 text-white/50 rounded">
												Locked
											</div>
										</Label>
										<Input
											id="email"
											placeholder="vivek@gmail.com"
											className={cn(
												"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
												field.disabled && "opacity-50",
											)}
											disabled
											{...field}
										/>
										<FormMessage className="text-red-500 text-sm mt-1" />
									</FormItem>
								)}
							/>
						</div>
					</CardContent>
				</Card>
			</form>
		</Form>
	);
}
